(() => {
  try {
    const MOBILE_MQ = "(max-width: 989px)";
    const isMobile = () => window.matchMedia(MOBILE_MQ).matches;

    const STATE = {
      sizeUserIntent: false,
      stickyTriggeredAtc: false,
      observer: null,
      metaObserver: null,
    };

    /* =========================
       Toast (inline styles)
       Compact pill style
       - mobile: centered above sticky bar
       - desktop: centered on screen
    ========================= */
    function ensureToast() {
      let toast = document.querySelector(".dm-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.className = "dm-toast";

        toast.style.position = "fixed";
        toast.style.zIndex = "999999";
        toast.style.background = "rgba(20,20,20,0.95)";
        toast.style.color = "#fff";
        toast.style.padding = "10px 14px";
        toast.style.borderRadius = "999px";
        toast.style.fontSize = "13px";
        toast.style.fontWeight = "600";
        toast.style.lineHeight = "1.2";
        toast.style.pointerEvents = "none";
        toast.style.opacity = "0";
        toast.style.transition = "opacity 150ms ease, transform 150ms ease";
        toast.style.whiteSpace = "nowrap";
        toast.style.maxWidth = "90vw";
        toast.style.overflow = "hidden";
        toast.style.textOverflow = "ellipsis";
        toast.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)";

        document.body.appendChild(toast);
      }

      if (isMobile()) {
        toast.style.left = "50%";
        toast.style.top = "auto";
        toast.style.right = "auto";
        toast.style.bottom = "96px"; // above sticky ATC
        toast.style.transform = "translateX(-50%)";
        toast.style.textAlign = "center";
      } else {
        toast.style.left = "50%";
        toast.style.top = "50%";
        toast.style.right = "auto";
        toast.style.bottom = "auto";
        toast.style.transform = "translate(-50%, -50%)";
        toast.style.textAlign = "center";
      }

      return toast;
    }

    function showToast(message, ms = 1600) {
      const toast = ensureToast();
      toast.textContent = message;
      toast.style.opacity = "1";
      clearTimeout(toast._timer);
      toast._timer = setTimeout(() => {
        toast.style.opacity = "0";
      }, ms);
    }

    /* =========================
       Helpers
    ========================= */
    function normalize(s) {
      return String(s || "").replace(/\s+/g, " ").trim().toLowerCase();
    }
    function cleanText(s) {
      return String(s || "").replace(/\s+/g, " ").trim();
    }

    /* =========================
       Fieldsets helpers
    ========================= */
    function findFieldsetByLegend(scopeEl, legendLowerExact) {
      const fieldsets = Array.from(scopeEl.querySelectorAll("fieldset"));
      return (
        fieldsets.find((fs) => {
          const lg = fs.querySelector("legend");
          return lg && normalize(lg.textContent) === legendLowerExact;
        }) || null
      );
    }

    function findFieldsetByLegendIncludes(scopeEl, needleLower) {
      const fieldsets = Array.from(scopeEl.querySelectorAll("fieldset"));
      return (
        fieldsets.find((fs) => {
          const lg = fs.querySelector("legend");
          return lg && normalize(lg.textContent).includes(needleLower);
        }) || null
      );
    }

    function findSizeFieldset(scopeEl) {
      return (
        findFieldsetByLegend(scopeEl, "size") ||
        findFieldsetByLegendIncludes(scopeEl, "size") ||
        null
      );
    }

    function findColorFieldset(scopeEl) {
      return (
        findFieldsetByLegend(scopeEl, "color") ||
        findFieldsetByLegend(scopeEl, "colour") ||
        findFieldsetByLegendIncludes(scopeEl, "color") ||
        findFieldsetByLegendIncludes(scopeEl, "colour") ||
        null
      );
    }

    function getSelectedValueFromFieldset(fs) {
      if (!fs) return "";

      const checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) {
        const v = cleanText(checked.value);
        if (v) return v;

        if (checked.id) {
          const label = document.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
          const txt = cleanText(label?.textContent);
          if (txt) return txt;
        }
      }

      const select = fs.querySelector("select");
      if (select) {
        const opt = select.selectedOptions?.[0];
        return cleanText(opt?.textContent || select.value);
      }

      return "";
    }

    /* =========================
       ProductJson label (most reliable)
    ========================= */
    function getSelectedVariantIdFromForm(form) {
      const idInput = form?.querySelector('input[name="id"]');
      return idInput?.value ? String(idInput.value) : "";
    }

    function parseProductJsonNear(productFormComponent) {
      const scope =
        productFormComponent?.closest("section") ||
        productFormComponent?.closest(".product") ||
        document;

      const script =
        scope.querySelector('script[type="application/json"][id^="ProductJson-"]') ||
        document.querySelector('script[type="application/json"][id^="ProductJson-"]');

      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    }

    function buildLabelFromProductJson(productData, variantId) {
      if (!productData || !Array.isArray(productData.variants) || !variantId) return "";

      const v = productData.variants.find((x) => String(x.id) === String(variantId));
      if (!v) return "";

      const optNames = Array.isArray(productData.options) ? productData.options : [];
      const names = optNames.map((o) => normalize(o));

      const map = {};
      if (names[0] && v.option1) map[names[0]] = v.option1;
      if (names[1] && v.option2) map[names[1]] = v.option2;
      if (names[2] && v.option3) map[names[2]] = v.option3;

      const color = map["color"] || map["colour"] || "";
      const size = map["size"] || "";

      const primary = [color, size].filter(Boolean).join(" / ");
      if (primary) return primary;

      return [v.option1, v.option2].filter(Boolean).join(" / ");
    }

    function buildLabel(scopeEl, productFormComponent, form) {
      try {
        const productData = parseProductJsonNear(productFormComponent);
        const variantId = getSelectedVariantIdFromForm(form);
        const fromJson = buildLabelFromProductJson(productData, variantId);
        if (fromJson) return fromJson;
      } catch {
        // ignore
      }

      const colorFs = findColorFieldset(scopeEl);
      const sizeFs = findSizeFieldset(scopeEl);
      const color = getSelectedValueFromFieldset(colorFs);
      const size = getSelectedValueFromFieldset(sizeFs);
      return [color, size].filter(Boolean).join(" / ");
    }

    /* =========================
       Size intent (USER-ONLY)
    ========================= */
    function bindSizeIntent(scopeEl) {
      if (scopeEl.dataset.dmSizeIntentBound) return;
      scopeEl.dataset.dmSizeIntentBound = "true";

      const sizeFs = findSizeFieldset(scopeEl);
      if (!sizeFs) return;

      const markIfTrusted = (e) => {
        if (!e.isTrusted) return;
        STATE.sizeUserIntent = true;
      };

      sizeFs.addEventListener("click", markIfTrusted, true);
      sizeFs.addEventListener("change", markIfTrusted, true);
    }

    function focusSize(scopeEl) {
      const sizeFs = findSizeFieldset(scopeEl);
      if (!sizeFs) return;

      sizeFs.scrollIntoView({ behavior: "smooth", block: "center" });
      sizeFs.classList.add("dm-size-pulse");
      setTimeout(() => sizeFs.classList.remove("dm-size-pulse"), 900);
    }

    /* =========================
       Sticky bar: correct product price (scoped)
    ========================= */
    function getProductPriceText(productFormComponent) {
      if (!productFormComponent) return "";

      const root =
        productFormComponent.closest("product-info") ||
        productFormComponent.closest(".product") ||
        productFormComponent;

      const priceEl = root.querySelector(
        '.price__sale .price-item--sale, ' +
          '.price__regular .price-item--regular, ' +
          '.price-item--sale, ' +
          '.price-item--regular'
      );

      return priceEl ? cleanText(priceEl.textContent) : "";
    }

    function updateStickyPrice(bar, productFormComponent) {
      if (!bar) return;
      const el = bar.querySelector(".dm-sticky-atc__price");
      if (!el) return;

      const next = getProductPriceText(productFormComponent);
      if (next) el.textContent = next;
    }

    function bindStickyPriceAutoUpdate(bar, productFormComponent) {
      // Disconnect previous observer if any
      STATE.metaObserver?.disconnect?.();
      STATE.metaObserver = null;

      const root =
        productFormComponent.closest("product-info") ||
        productFormComponent.closest(".product") ||
        productFormComponent;

      const findLivePriceEl = () =>
        root.querySelector(
          '.price__sale .price-item--sale, ' +
            '.price__regular .price-item--regular, ' +
            '.price-item--sale, ' +
            '.price-item--regular, ' +
            '.price'
        );

      const connect = () => {
        const live = findLivePriceEl();
        if (!live) return false;

        const obs = new MutationObserver(() => {
          requestAnimationFrame(() => updateStickyPrice(bar, productFormComponent));
        });

        obs.observe(live, { childList: true, subtree: true, characterData: true });
        STATE.metaObserver = obs;
        return true;
      };

      // Initial update + try connect immediately
      updateStickyPrice(bar, productFormComponent);
      if (connect()) return;

      // If Dawn swaps nodes, price might not exist yet. Retry briefly.
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        updateStickyPrice(bar, productFormComponent);
        if (connect() || tries >= 12) clearInterval(timer);
      }, 150);
    }

    function removeSticky() {
      STATE.observer?.disconnect?.();
      STATE.observer = null;

      STATE.metaObserver?.disconnect?.();
      STATE.metaObserver = null;

      document.querySelector(".dm-sticky-atc")?.remove();
      document.body.classList.remove("dm-has-sticky-atc");
    }

    function buildStickyBar(productFormComponent) {
      const title = document.querySelector("h1")?.textContent || "";
      const price = getProductPriceText(productFormComponent);

      const bar = document.createElement("div");
      bar.className = "dm-sticky-atc";
      bar.innerHTML = `
        <div class="dm-sticky-atc__row">
          <div class="dm-sticky-atc__meta">
            <div class="dm-sticky-atc__title">${title}</div>
            <div class="dm-sticky-atc__price">${price}</div>
          </div>
          <button class="dm-sticky-atc__btn" type="button">Add to cart</button>
        </div>
      `;
      return bar;
    }

    function initStickyATC() {
      removeSticky();
      if (!isMobile()) return;

      const productFormComponent = document.querySelector("product-form");
      if (!productFormComponent) return;

      const scopeEl =
        productFormComponent.closest("section") ||
        productFormComponent.closest(".product") ||
        document;

      bindSizeIntent(scopeEl);

      const form = productFormComponent.querySelector('form[action^="/cart/add"]');
      if (!form) return;

      const mainBtn = form.querySelector("button.product-form__submit");
      if (!mainBtn) return;

      const anchor = productFormComponent.querySelector(".product-form__buttons") || mainBtn;

      const bar = buildStickyBar(productFormComponent);
      document.body.appendChild(bar);
      document.body.classList.add("dm-has-sticky-atc");

      const stickyBtn = bar.querySelector(".dm-sticky-atc__btn");

      stickyBtn.addEventListener("click", () => {
        const hasSize = !!findSizeFieldset(scopeEl);

        if (hasSize && !STATE.sizeUserIntent) {
          focusSize(scopeEl);
          showToast("Choose a size to add to cart");
          return;
        }

        STATE.stickyTriggeredAtc = true;
        anchor.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          mainBtn.click();
          setTimeout(() => (STATE.stickyTriggeredAtc = false), 600);
        }, 250);
      });

      // Keep sticky price synced to actual variant price
      bindStickyPriceAutoUpdate(bar, productFormComponent);

      // Also nudge-update after option changes (covers very fast swaps)
      scopeEl.addEventListener(
        "change",
        () => {
          updateStickyPrice(bar, productFormComponent);
          setTimeout(() => updateStickyPrice(bar, productFormComponent), 60);
          setTimeout(() => updateStickyPrice(bar, productFormComponent), 220);
        },
        true
      );

// Show the sticky ATC only after the regular ATC
// has completely passed above the viewport.
let lastShow = null;
let toggleTimer = null;

STATE.observer = new IntersectionObserver(
  ([entry]) => {
    const viewportTop = entry.rootBounds?.top ?? 0;

    const regularAtcHasPassed =
      entry.boundingClientRect.bottom <= viewportTop;

    const shouldShowSticky =
      !entry.isIntersecting && regularAtcHasPassed;

    if (lastShow === shouldShowSticky) return;
    lastShow = shouldShowSticky;

    clearTimeout(toggleTimer);
    toggleTimer = setTimeout(() => {
      bar.classList.toggle(
        "dm-sticky-atc--visible",
        shouldShowSticky
      );
    }, 120);
  },
  {
    threshold: [0, 0.01],
    rootMargin: "0px",
  }
);

STATE.observer.observe(anchor);
        }

    /* =========================
       Regular ATC toast (delegated) — desktop + mobile
       Shows ONLY in the "default size added" scenario:
       - product has Size
       - user never touched Size
       - click is a real user click (not sticky triggering mainBtn.click())
    ========================= */
    function bindRegularAtcToastOnce() {
      if (document.documentElement.dataset.dmAtcToastBound) return;
      document.documentElement.dataset.dmAtcToastBound = "true";

      document.addEventListener(
        "click",
        (e) => {
          const btn = e.target.closest("button.product-form__submit");
          if (!btn) return;

          if (!e.isTrusted) return;
          if (STATE.stickyTriggeredAtc) return;

          const productFormComponent = document.querySelector("product-form");
          if (!productFormComponent) return;

          const scopeEl =
            productFormComponent.closest("section") ||
            productFormComponent.closest(".product") ||
            document;

          const hasSize = !!findSizeFieldset(scopeEl);
          if (!hasSize) return;
          if (STATE.sizeUserIntent) return;

          const form = btn.closest("form");

          setTimeout(() => {
            const label = buildLabel(scopeEl, productFormComponent, form);
            showToast(label ? `Added to cart: ${label}` : "Added to cart");
          }, 200);
        },
        true
      );
    }

    /* =========================
       Boot
    ========================= */
    window.addEventListener("DOMContentLoaded", () => {
      bindRegularAtcToastOnce();
      initStickyATC();
    });

    document.addEventListener("shopify:section:load", initStickyATC);

    let wasMobile = isMobile();

window.addEventListener("resize", () => {
  const nowMobile = isMobile();

  // Rebuild the sticky ATC only when crossing
  // the desktop/mobile breakpoint.
  if (nowMobile !== wasMobile) {
    wasMobile = nowMobile;

    if (nowMobile) {
      initStickyATC();
    } else {
      removeSticky();
    }
  }

  const toast = document.querySelector(".dm-toast");
  if (toast) ensureToast();
});

  } catch (err) {
    try {
      const div = document.createElement("div");
      div.style.position = "fixed";
      div.style.top = "8px";
      div.style.left = "8px";
      div.style.right = "8px";
      div.style.zIndex = "999999";
      div.style.background = "rgba(160,0,0,0.9)";
      div.style.color = "#fff";
      div.style.padding = "8px 10px";
      div.style.borderRadius = "10px";
      div.style.fontSize = "12px";
      div.textContent = "Sticky ATC error: " + (err?.message || "unknown");
      document.body.appendChild(div);
    } catch {
      /* ignore */
    }
  }
})();
